import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api/client';
import { usePrintHeader } from '../../hooks/usePrintHeader';
import { useSettings } from '../../hooks/useSettings';
import { addressLine, date, fine, money, rate, weight } from '../../utils/format';
import { ErrorNotice, Loading } from '../../components/ui';
import { Breakup, PrintDocument, PrintToolbar } from '../../components/print/PrintDocument';

interface Line {
  line_no: number;
  item_code: string | null;
  product_name: string | null;
  description: string | null;
  purity_code: string | null;
  gross_wt: string;
  stone_wt?: string | null;
  wastage_wt?: string | null;
  net_wt: string;
  fine_wt: string;
  rate_value: string;
  metal_value: string;
  making_amount: string;
  stone_amount: string;
  taxable_value: string;
  tax_amount: string;
  line_total: string;
}

interface Estimate {
  doc_no: string;
  doc_date: string;
  party_name: string;
  customer_phone: string | null;
  customer_address?: unknown;
  branch_name?: string;
  status: string;
  valid_until: string | null;
  taxable_value: string;
  tax_total: string;
  round_off: string;
  grand_total: string;
  sale_doc_no: string | null;
  notes: string | null;
  lines: Line[];
}

/**
 * The printed quote a customer takes away.
 *
 * Laid out exactly like the shop's bill — same header, same columns, same
 * totals — so the customer can lay the two side by side and see that nothing
 * changed between the promise and the invoice. It says plainly that it is not a
 * bill, that no GST is payable on it, and until when the price holds, because a
 * quote a customer believes is a bill is how arguments start.
 */
export function EstimatePrintPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setting } = useSettings();
  const { data, isLoading, error } = useQuery({
    queryKey: ['estimate', id],
    queryFn: () => api.get<Estimate>(`/estimates/${id}`),
  });
  const { header } = usePrintHeader(data?.branch_name);

  if (isLoading) return <Loading rows={8} />;
  if (error || !data) return <ErrorNotice error={error} />;

  // A document that arrives without its lines still prints its header and
  // totals rather than emptying the screen.
  const lines = data.lines ?? [];

  const totals = [
    { label: 'Taxable value', value: money(data.taxable_value) },
    { label: 'GST', value: money(data.tax_total) },
    ...(Number(data.round_off) !== 0 ? [{ label: 'Round off', value: money(data.round_off) }] : []),
  ];

  return (
    <>
      <PrintToolbar onBack={() => navigate('/estimates')} fileName={data.doc_no} />

      <PrintDocument
        title={data.status === 'CONVERTED' ? 'Estimate (billed)' : 'Estimate'}
        header={header}
        docNo={data.doc_no}
        docDate={date(data.doc_date)}
        party={{
          name: data.party_name,
          phone: data.customer_phone,
          address: addressLine(data.customer_address) || null,
        }}
        partyLabel="Quoted to"
        details={[
          { label: 'Valid until', value: data.valid_until ? date(data.valid_until) : 'Today’s rate only' },
          { label: 'Pieces', value: String(lines.length) },
          { label: 'Net weight', value: weight(lines.reduce((a, l) => a + Number(l.net_wt), 0).toFixed(3)) },
          ...(data.sale_doc_no ? [{ label: 'Billed as', value: data.sale_doc_no }] : []),
        ]}
        grandTotalLabel="Estimated total"
        totals={totals}
        grandTotal={data.grand_total}
        footNote={[
          'This is an estimate, not a tax invoice. No GST is payable on this document and no stock has been set aside.',
          data.valid_until
            ? `This price holds until ${date(data.valid_until)}.`
            : 'Prices follow the metal rate of the day and may change.',
          data.sale_doc_no ? `Billed as ${data.sale_doc_no}.` : '',
          data.notes ?? '',
          setting('documents.invoice_terms'),
        ]
          .filter(Boolean)
          .join('\n')}
      >
        <table className="doc-table">
          <thead>
            <tr>
              <th className="w-6">#</th>
              <th>Item</th>
              <th className="c">Purity</th>
              <th className="r">Net wt</th>
              <th className="r">Fine wt</th>
              <th className="r">Rate / g</th>
              <th className="r">Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => {
              const parts = [
                `Gross ${weight(l.gross_wt)}`,
                Number(l.stone_wt ?? 0) > 0 ? `Stone wt ${weight(l.stone_wt)}` : null,
                `Metal ${money(l.metal_value)}`,
                Number(l.wastage_wt ?? 0) > 0 ? `Wastage ${fine(l.wastage_wt)}` : null,
                Number(l.making_amount) > 0 ? `Making ${money(l.making_amount)}` : null,
                Number(l.stone_amount) > 0 ? `Stones ${money(l.stone_amount)}` : null,
                Number(l.tax_amount) > 0 ? `GST ${money(l.tax_amount)}` : null,
              ].filter(Boolean);
              return (
                <tr key={l.line_no}>
                  <td className="num !text-left">{l.line_no}</td>
                  <td>
                    <span className="font-medium">{l.product_name ?? l.description ?? 'Item'}</span>
                    {l.item_code ? (
                      <span className="block font-mono text-[11px] text-ink-soft">{l.item_code}</span>
                    ) : null}
                    <Breakup parts={parts as string[]} />
                  </td>
                  <td className="c num font-semibold">{l.purity_code ?? '—'}</td>
                  <td className="num">{weight(l.net_wt)}</td>
                  <td className="num">{fine(l.fine_wt)}</td>
                  <td className="num">{rate(l.rate_value)}</td>
                  <td className="num font-semibold">{money(l.line_total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </PrintDocument>
    </>
  );
}
